import React, { useEffect, useState, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useAutosaveIndicator } from "@/hooks/useAutosaveIndicator";
import { SaveChip } from "@/components/ui/SaveChip";

const sb: any = supabase as any;

type Sensitivity = "Low" | "Moderate" | "High" | "Critical";
type Volume = "Small" | "Moderate" | "Large" | "Very Large";

interface DataTypeConfig {
  label: string;
  defaultSensitivity: Sensitivity;
}

const TELECOM_DATA_TYPES: DataTypeConfig[] = [
  { label: "Personally Identifiable Information (PII)", defaultSensitivity: "High" },
  { label: "Customer Account Data", defaultSensitivity: "High" },
  { label: "Subscriber Metadata / Call Detail Records", defaultSensitivity: "High" },
  { label: "Network Traffic Data", defaultSensitivity: "High" },
  { label: "Authentication Credentials / Identity Data", defaultSensitivity: "Critical" },
  { label: "Billing and Financial Records", defaultSensitivity: "High" },
  { label: "Payment Card Information", defaultSensitivity: "Critical" },
  { label: "Telecom Network Infrastructure Data", defaultSensitivity: "Critical" },
  { label: "Operational Network Configuration Data", defaultSensitivity: "Critical" },
  { label: "Lawful Intercept / Regulatory Data", defaultSensitivity: "Critical" },
  { label: "Internal Corporate Data", defaultSensitivity: "Moderate" },
  { label: "Vendor / Partner Data", defaultSensitivity: "Moderate" },
  { label: "Intellectual Property / Proprietary Technology", defaultSensitivity: "High" },
];

const SENSITIVITY_OPTIONS: Sensitivity[] = ["Low", "Moderate", "High", "Critical"];
const VOLUME_OPTIONS: Volume[] = ["Small", "Moderate", "Large", "Very Large"];

const SENSITIVITY_COLORS: Record<Sensitivity, string> = {
  Low: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300",
  Moderate: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  High: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300",
  Critical: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
};

interface ClassificationRow {
  data_type: string;
  default_sensitivity: string | null;
  reviewer_sensitivity: string | null;
  data_volume: string | null;
  reviewer_notes: string | null;
  reviewed: boolean;
}

interface Props {
  assessmentId: string;
  disabled?: boolean;
}

export const DataClassificationTab: React.FC<Props> = ({ assessmentId, disabled }) => {
  const [rows, setRows] = useState<Map<string, ClassificationRow>>(new Map());
  const [loading, setLoading] = useState(true);
  const autosave = useAutosaveIndicator();

  // Load existing data
  useEffect(() => {
    if (!assessmentId) return;
    (async () => {
      setLoading(true);
      const { data, error } = await sb.rpc("reviewer_get_data_classification", {
        p_assessment_id: assessmentId,
      });
      if (!error && data) {
        const map = new Map<string, ClassificationRow>();
        for (const r of data) {
          map.set(r.data_type, {
            data_type: r.data_type,
            default_sensitivity: r.default_sensitivity,
            reviewer_sensitivity: r.reviewer_sensitivity,
            data_volume: r.data_volume,
            reviewer_notes: r.reviewer_notes ?? null,
            reviewed: !!r.reviewed,
          });
        }
        setRows(map);
      }
      setLoading(false);
    })();
  }, [assessmentId]);

  const upsert = useCallback(
    async (
      dataType: string,
      defaultSens: string,
      reviewerSens: string | null,
      volume: string | null,
      notes: string | null,
    ) => {
      autosave.begin();
      const { error } = await sb.rpc("reviewer_upsert_data_classification", {
        p_assessment_id: assessmentId,
        p_data_type: dataType,
        p_default_sensitivity: defaultSens,
        p_reviewer_sensitivity: reviewerSens,
        p_data_volume: volume,
        p_reviewer_notes: notes,
      });
      if (error) {
        console.error("upsert classification error", error);
        autosave.fail();
      } else {
        autosave.succeed();
      }
    },
    [assessmentId, autosave]
  );

  const remove = useCallback(
    async (dataType: string) => {
      autosave.begin();
      const { error } = await sb.rpc("reviewer_delete_data_classification", {
        p_assessment_id: assessmentId,
        p_data_type: dataType,
      });
      if (error) {
        console.error("delete classification error", error);
        autosave.fail();
      } else {
        autosave.succeed();
      }
    },
    [assessmentId, autosave]
  );

  const setReviewed = useCallback(
    async (dataType: string, reviewed: boolean) => {
      autosave.begin();
      const { error } = await sb.rpc("reviewer_set_data_classification_reviewed", {
        p_assessment_id: assessmentId,
        p_data_type: dataType,
        p_reviewed: reviewed,
      });
      if (error) {
        console.error("set reviewed error", error);
        autosave.fail();
      } else {
        autosave.succeed();
      }
    },
    [assessmentId, autosave]
  );

  const handleToggle = useCallback(
    (config: DataTypeConfig, checked: boolean) => {
      setRows((prev) => {
        const next = new Map(prev);
        if (checked) {
          const row: ClassificationRow = {
            data_type: config.label,
            default_sensitivity: config.defaultSensitivity,
            reviewer_sensitivity: config.defaultSensitivity,
            data_volume: null,
            reviewer_notes: null,
            reviewed: false,
          };
          next.set(config.label, row);
          upsert(config.label, config.defaultSensitivity, config.defaultSensitivity, null, null);
        } else {
          next.delete(config.label);
          remove(config.label);
        }
        return next;
      });
    },
    [upsert, remove]
  );

  const handleSensitivityChange = useCallback(
    (dataType: string, value: string) => {
      setRows((prev) => {
        const next = new Map(prev);
        const existing = next.get(dataType);
        if (existing) {
          const updated = { ...existing, reviewer_sensitivity: value };
          next.set(dataType, updated);
          upsert(dataType, existing.default_sensitivity ?? value, value, existing.data_volume, existing.reviewer_notes);
        }
        return next;
      });
    },
    [upsert]
  );

  const handleVolumeChange = useCallback(
    (dataType: string, value: string) => {
      setRows((prev) => {
        const next = new Map(prev);
        const existing = next.get(dataType);
        if (existing) {
          const updated = { ...existing, data_volume: value };
          next.set(dataType, updated);
          upsert(dataType, existing.default_sensitivity ?? "", existing.reviewer_sensitivity, value, existing.reviewer_notes);
        }
        return next;
      });
    },
    [upsert]
  );

  const handleNotesChange = useCallback(
    (dataType: string, value: string) => {
      setRows((prev) => {
        const next = new Map(prev);
        const existing = next.get(dataType);
        if (existing) {
          next.set(dataType, { ...existing, reviewer_notes: value });
        }
        return next;
      });
    },
    []
  );

  const handleNotesBlur = useCallback(
    (dataType: string) => {
      const existing = rows.get(dataType);
      if (!existing) return;
      upsert(
        dataType,
        existing.default_sensitivity ?? "",
        existing.reviewer_sensitivity,
        existing.data_volume,
        existing.reviewer_notes,
      );
    },
    [rows, upsert]
  );

  const handleReviewedToggle = useCallback(
    (dataType: string, value: boolean) => {
      setRows((prev) => {
        const next = new Map(prev);
        const existing = next.get(dataType);
        if (existing) {
          next.set(dataType, { ...existing, reviewed: value });
          setReviewed(dataType, value);
        }
        return next;
      });
    },
    [setReviewed]
  );

  const progress = useMemo(() => {
    const selected = rows.size;
    const reviewed = Array.from(rows.values()).filter((r) => r.reviewed).length;
    return { selected, reviewed };
  }, [rows]);

  const allReviewed = progress.selected > 0 && progress.reviewed === progress.selected;

  if (loading) {
    return (
      <div className="bg-card rounded-lg border p-8 text-center text-muted-foreground">
        Loading data classification…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">Data Classification</h2>
          <p className="text-sm text-muted-foreground">
            Identify what types of sensitive data the organization stores or processes,
            then mark each row Reviewed once classification, volume and notes are validated.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <Badge
            variant="outline"
            className={
              progress.selected === 0
                ? "bg-muted text-muted-foreground"
                : allReviewed
                ? "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-300"
                : "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300"
            }
          >
            {progress.selected === 0
              ? "No rows selected"
              : `${progress.reviewed} / ${progress.selected} reviewed`}
          </Badge>
          <SaveChip state={autosave.state} />
        </div>
      </div>

      <div className="space-y-3">
        {TELECOM_DATA_TYPES.map((config) => {
          const isChecked = rows.has(config.label);
          const row = rows.get(config.label);

          return (
            <div
              key={config.label}
              className={`rounded-lg border p-4 transition-colors ${
                isChecked
                  ? row?.reviewed
                    ? "bg-card border-emerald-300 dark:border-emerald-700"
                    : "bg-card border-primary/30"
                  : "bg-muted/30 border-border"
              }`}
            >
              <div className="flex items-start gap-3">
                <Checkbox
                  checked={isChecked}
                  disabled={disabled}
                  onCheckedChange={(checked) => handleToggle(config, !!checked)}
                  className="mt-1"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`font-medium text-sm ${isChecked ? "text-foreground" : "text-muted-foreground"}`}>
                      {config.label}
                    </span>
                    <Badge
                      variant="outline"
                      className={`text-[11px] ${SENSITIVITY_COLORS[config.defaultSensitivity]}`}
                    >
                      Default: {config.defaultSensitivity}
                    </Badge>
                    {isChecked && row?.reviewed && (
                      <Badge
                        variant="outline"
                        className="text-[11px] bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-300"
                      >
                        Reviewed
                      </Badge>
                    )}
                  </div>

                  {isChecked && (
                    <div className="mt-3 space-y-3">
                      <div className="flex flex-wrap gap-4">
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground font-medium">
                            Reviewer Sensitivity
                          </label>
                          <Select
                            value={row?.reviewer_sensitivity ?? ""}
                            onValueChange={(v) => handleSensitivityChange(config.label, v)}
                            disabled={disabled}
                          >
                            <SelectTrigger className="w-[140px] h-8 text-xs">
                              <SelectValue placeholder="Select…" />
                            </SelectTrigger>
                            <SelectContent>
                              {SENSITIVITY_OPTIONS.map((opt) => (
                                <SelectItem key={opt} value={opt}>
                                  {opt}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground font-medium">
                            Data Volume
                          </label>
                          <Select
                            value={row?.data_volume ?? ""}
                            onValueChange={(v) => handleVolumeChange(config.label, v)}
                            disabled={disabled}
                          >
                            <SelectTrigger className="w-[140px] h-8 text-xs">
                              <SelectValue placeholder="Select…" />
                            </SelectTrigger>
                            <SelectContent>
                              {VOLUME_OPTIONS.map((opt) => (
                                <SelectItem key={opt} value={opt}>
                                  {opt}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-1 ml-auto">
                          <label className="text-xs text-muted-foreground font-medium">
                            Reviewed
                          </label>
                          <div className="h-8 flex items-center gap-2">
                            <Switch
                              checked={!!row?.reviewed}
                              disabled={disabled}
                              onCheckedChange={(v) => handleReviewedToggle(config.label, !!v)}
                            />
                            <span className="text-xs text-muted-foreground">
                              {row?.reviewed ? "Reviewed" : "Draft"}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <label className="text-xs text-muted-foreground font-medium">
                          Reviewer Notes
                        </label>
                        <Textarea
                          value={row?.reviewer_notes ?? ""}
                          onChange={(e) => handleNotesChange(config.label, e.target.value)}
                          onBlur={() => handleNotesBlur(config.label)}
                          disabled={disabled}
                          placeholder="Optional context, scope clarification, or evidence reference…"
                          className="text-xs min-h-[64px]"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
